import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { TIDS } from "@/constants/testIds";

/**
 * SquareCardForm: loads Web Payments SDK and returns a tokenized nonce on submit.
 * Props: { config: {environment, application_id, location_id, configured}, amountCents, onToken }
 */
export default function SquareCardForm({ config, amountCents, onToken, disabled }) {
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!config || !config.configured) return;
    const sdkUrl =
      String(config.environment).toUpperCase() === "PRODUCTION"
        ? "https://web.squarecdn.com/v1/square.js"
        : "https://sandbox.web.squarecdn.com/v1/square.js";

    let cancelled = false;
    const existing = document.querySelector(`script[data-square-sdk="1"]`);
    const ensure = () =>
      new Promise((resolve, reject) => {
        if (window.Square) return resolve();
        if (existing) {
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener("error", reject, { once: true });
          return;
        }
        const s = document.createElement("script");
        s.src = sdkUrl;
        s.async = true;
        s.dataset.squareSdk = "1";
        s.onload = () => resolve();
        s.onerror = reject;
        document.body.appendChild(s);
      });

    (async () => {
      try {
        await ensure();
        if (cancelled || !window.Square) return;
        const payments = window.Square.payments(config.application_id, config.location_id);
        const cardMethod = await payments.card();
        await cardMethod.attach(containerRef.current);
        setCard(cardMethod);
      } catch (e) {
        setStatus("Card form failed to load. Please refresh, or pay the main office by cash or check.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config]);

  const submit = async () => {
    if (!card) return;
    setBusy(true);
    setStatus("");
    try {
      const verification = {
        amount: (amountCents / 100).toFixed(2),
        currencyCode: "USD",
        intent: "CHARGE",
        customerInitiated: true,
        sellerKeyedIn: false,
      };
      const result = await card.tokenize(verification);
      if (result.status !== "OK") {
        throw new Error((result.errors && result.errors[0]?.message) || "Card tokenization failed");
      }
      await onToken(result.token);
    } catch (e) {
      setStatus(e.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  if (!config || !config.configured) {
    return (
      <div className="rrdc-card p-4 text-sm text-[#4B5563]">
        Card payments are not enabled yet. Please use the pay main office option below.
      </div>
    );
  }

  return (
    <div className="rrdc-card p-4">
      <div ref={containerRef} id="square-card-container" />
      <Button
        type="button"
        onClick={submit}
        disabled={busy || disabled || !card}
        className="mt-4 w-full h-12 rrdc-btn-primary rounded-xl text-base font-semibold"
        data-testid={TIDS.paySubmit}
      >
        {busy ? "Processing" : `Pay $${(amountCents / 100).toFixed(2)} by card`}
      </Button>
      {status ? <div className="mt-3 text-sm text-[#B91C1C]">{status}</div> : null}
    </div>
  );
}
